class CoastPickerPopup extends ModalDialog {
  constructor() {
    super();
    this.init();
    this.attachEventListeners();
  }

  init() {
    this.delay = this.dataset.delay * 1000;
    this.currentCoast = this.dataset.currentCoast;
    this.audience = this.dataset.audience;
    this.cookieName = "coast-picker-closed";
    this.rootDomain = "mickfanningsoftboards.com";
  }

  attachEventListeners() {
    // ModalDialog already wires the stay/ModalClose button, overlay click
    // and ESC to hide(), which we override below — only the switch button
    // needs its own listener.
    this.querySelectorAll(".js-coast-switch").forEach(button =>
      button.addEventListener("click", this.onSwitch.bind(this))
    );
  }

  // 30-day cookie on the shared root domain so BOTH US stores see the
  // answer and don't re-ask (no popup loops, no per-visit nagging).
  // Falls back to a host-only cookie on preview/myshopify hosts.
  setClosedCookie() {
    const domain =
      window.location.hostname.indexOf(this.rootDomain) !== -1
        ? "; domain=." + this.rootDomain
        : "";
    const maxAge = 30 * 24 * 60 * 60;
    document.cookie =
      this.cookieName + "=true; path=/" + domain +
      "; max-age=" + maxAge + "; SameSite=Lax";
  }

  // Visitor's likely coast from the browser timezone — free, instant, no
  // geo API. Pacific/Mountain/Alaska/Hawaii → west, Eastern/Central → east
  // (same split the old geo-redirect used). Returns null for non-US.
  detectCoast() {
    let tz = "";
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch (error) {
      return null;
    }
    if (
      /^America\/(Los_Angeles|Denver|Phoenix|Boise|Anchorage|Juneau|Sitka|Metlakatla|Yakutat|Nome|Adak)$/.test(tz) ||
      tz === "Pacific/Honolulu" ||
      /^US\/(Pacific|Mountain|Arizona|Alaska|Hawaii|Aleutian)$/.test(tz)
    ) {
      return "west";
    }
    if (
      /^America\/(New_York|Detroit|Chicago|Menominee)$/.test(tz) ||
      /^America\/(Indiana|Kentucky|North_Dakota)\//.test(tz) ||
      /^US\/(Eastern|Central|East-Indiana|Michigan|Indiana-Starke)$/.test(tz)
    ) {
      return "east";
    }
    return null;
  }

  connectedCallback() {
    if (Shopify.designMode) {
      if (this.dataset.openInDesignMode === "true") {
        this.show();
      }
      return;
    }

    // Never show to search-engine / AI crawlers (Googlebot renders JS from
    // US datacenters and would otherwise see an interstitial on every page).
    if (/bot|crawl|spider|slurp|mediapartners|bingpreview|facebookexternalhit|embedly|quora|pinterest|whatsapp|telegram|slackbot|flipboard|tumblr|google|baidu|yandex|duckduck|semrush|ahrefs|lighthouse|headless|preview/i.test(navigator.userAgent || "")) {
      return;
    }

    const params = new URLSearchParams(window.location.search);

    // Arrived via the other coast's popup → remember, clean URL, never show.
    if (params.get("coast") === "picked") {
      this.setClosedCookie();
      params.delete("coast");
      const clean =
        window.location.pathname +
        (params.toString() ? "?" + params.toString() : "") +
        window.location.hash;
      window.history.replaceState({}, "", clean);
      return;
    }

    // ?coast_popup=reset deletes the remembered answer, then the popup
    // behaves as if the visitor were brand new (for testers).
    if (params.get("coast_popup") === "reset") {
      const domain =
        window.location.hostname.indexOf(this.rootDomain) !== -1
          ? "; domain=." + this.rootDomain
          : "";
      document.cookie =
        this.cookieName + "=; path=/" + domain + "; max-age=0; SameSite=Lax";
    }

    // ?coast_popup=test force-shows regardless of cookie/timezone.
    const forceShow = params.get("coast_popup") === "test";
    if (!forceShow) {
      if (getCookie(this.cookieName) === "true") return;
      // Logged-in customer's address coast (from Liquid) wins over timezone.
      const detectedCoast = this.dataset.customerCoast || this.detectCoast();
      if (this.audience === "wrong-coast") {
        // Only interrupt visitors who appear to be on the WRONG store.
        // Right-coast, non-US and undetectable visitors shop uninterrupted.
        if (!detectedCoast || detectedCoast === this.currentCoast) return;
      } else if (this.audience === "us") {
        if (!detectedCoast) return;
      }
      // audience === "everyone": no gate.
    }

    setTimeout(() => this.show(), this.delay);
  }

  show(opener) {
    // Hide Shopify chat bubble on mobile so it doesn't appear behind the modal.
    const chatBubble = document.getElementById("shopify-chat");
    if (chatBubble) {
      this.chatBubbleOriginalDisplay = window.getComputedStyle(chatBubble).display;
      chatBubble.style.display = "none";
    }
    super.show(opener);
  }

  // Any dismissal (close link, overlay click, ESC) remembers the answer.
  hide() {
    // Restore Shopify chat bubble to its original display state.
    const chatBubble = document.getElementById("shopify-chat");
    if (chatBubble && this.chatBubbleOriginalDisplay) {
      chatBubble.style.display = this.chatBubbleOriginalDisplay;
    }
    this.setClosedCookie();
    super.hide();
  }

  onSwitch(event) {
    const button = event.currentTarget;
    this.setClosedCookie();
    if (!button.dataset.url) {
      this.hide();
      return;
    }
    const base = button.dataset.url.replace(/\/+$/, "");
    // coast=picked tells the destination store's coast-picker the visitor
    // already chose, so they aren't re-asked on arrival.
    // utm_source tags the switch so it shows up as its own traffic source
    // in Shopify/GA reports (a plain custom param wouldn't). The origin
    // store's coast is baked into the value, e.g. us-east-location-popup.
    const params = new URLSearchParams();
    params.set("coast", "picked");
    params.set("utm_source", "us-" + this.currentCoast + "-location-popup");
    window.location.href = base + "/?" + params.toString();
  }
}

customElements.define("coast-picker-popup", CoastPickerPopup);
